import React, { useState } from 'react';

import { Button, Group, Loader, Progress, Stack, Text } from '@mantine/core';
import { IconCheck, IconDownload, IconX } from '@tabler/icons-react';

import { plum } from '@/styles/colors';

import { BatchJobStatus } from '@/types/batchDownload';
import CustomModal from '@/ui/Modal';

import { BatchDownloadState } from './hooks/useNotDownloadedReports';

interface AsyncBatchDownloadModalProps {
  batchDownloadState: BatchDownloadState;
  setBatchDownloadState: React.Dispatch<React.SetStateAction<BatchDownloadState>>;
  onDownload: () => Promise<void>;
  onClose: () => void;
  notDownloadedCount: number;
  title?: string;
  downloadingMessage?: string;
  successMessage?: string;
}

export const AsyncBatchDownloadModal: React.FC<AsyncBatchDownloadModalProps> = ({
  batchDownloadState,
  onDownload,
  onClose,
  notDownloadedCount,
  title,
  downloadingMessage,
  successMessage
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleCloseAttempt = () => {
    if (batchDownloadState.status === BatchJobStatus.PROCESSING) {
      setShowConfirmation(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmation(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowConfirmation(false);
  };

  const getModalTitle = () => {
    if (title) {
      switch (batchDownloadState.status) {
        case BatchJobStatus.INITIALIZED:
        case BatchJobStatus.PROCESSING:
          return `Preparing ${title}`;
        case BatchJobStatus.SUCCESS:
          return `${title} Ready`;
        case BatchJobStatus.FAILED:
          return `${title} Failed`;
        default:
          return title;
      }
    }

    switch (batchDownloadState.status) {
      case BatchJobStatus.INITIALIZED:
      case BatchJobStatus.PROCESSING:
        return 'Preparing Reports';
      case BatchJobStatus.SUCCESS:
        return 'Download Ready';
      case BatchJobStatus.FAILED:
        return 'Download Failed';
      default:
        return 'Batch Download';
    }
  };

  const getProgressColor = () => {
    switch (batchDownloadState.status) {
      case BatchJobStatus.SUCCESS:
        return 'green';
      case BatchJobStatus.FAILED:
        return 'red';
      default:
        return plum[6];
    }
  };

  const renderProgressSection = () => {
    if (
      batchDownloadState.status === BatchJobStatus.INITIALIZED &&
      batchDownloadState.totalBatches === 0
    ) {
      return null;
    }

    return (
      <Stack gap="xs" w="70%" px="md">
        <Group justify="space-between" align="center">
          <Text size="sm" c="dimmed">
            Progress
          </Text>
          <Text size="sm" fw={500}>
            {Math.round(batchDownloadState.downloadProgress)}%
          </Text>
        </Group>

        <Progress
          value={batchDownloadState.downloadProgress}
          color={getProgressColor()}
          size="lg"
          radius="sm"
          striped={batchDownloadState.status === BatchJobStatus.PROCESSING}
          animated={batchDownloadState.status === BatchJobStatus.PROCESSING}
        />
      </Stack>
    );
  };

  const renderStatusIcon = () => {
    switch (batchDownloadState.status) {
      case BatchJobStatus.SUCCESS:
        return <IconCheck size={20} color="green" />;
      case BatchJobStatus.FAILED:
        return <IconX size={20} color="red" />;
      case BatchJobStatus.PROCESSING:
        return <Loader size="sm" color={plum[6]} />;
      default:
        return null;
    }
  };

  const renderModalContent = () => {
    switch (batchDownloadState.status) {
      case BatchJobStatus.INITIALIZED:
      case BatchJobStatus.PROCESSING:
        return (
          <>
            <Loader color={plum[6]} type="dots" mt="xl" />

            <Group justify="center" align="center" gap="md">
              <Stack gap={4} align="center">
                <Text size="md">
                  {downloadingMessage ||
                    `Processing ${notDownloadedCount} reports in ${batchDownloadState.totalBatches || 'multiple'} batches...`}
                </Text>
              </Stack>
            </Group>

            {renderProgressSection()}

            <Stack gap="xs" mt="md">
              {batchDownloadState.totalBatches > 3 && (
                <Text size="xs" c="orange" ta="center">
                  ⚡ High volume detected: This may take several minutes to complete
                </Text>
              )}
            </Stack>
          </>
        );

      case BatchJobStatus.SUCCESS:
        return (
          <>
            <Group justify="center" align="center" gap="md" mt="xl">
              {renderStatusIcon()}
              <Text size="md" fw={500}>
                {successMessage || 'All reports are ready for download!'}
              </Text>
            </Group>

            {renderProgressSection()}

            <Button
              leftSection={<IconDownload size={16} />}
              onClick={onDownload}
              loading={batchDownloadState.isDownloading}
              size="md"
              mt="lg">
              Download ZIP File
            </Button>
          </>
        );

      case BatchJobStatus.FAILED:
        return (
          <>
            <Group justify="center" align="center" gap="md" mt="xl">
              {renderStatusIcon()}
              <Text color="red" size="md">
                Download preparation failed
              </Text>
            </Group>

            {renderProgressSection()}

            <Text color="red" size="sm" ta="center" mt="md">
              {batchDownloadState.errorMessage ||
                'Something went wrong while preparing the reports.'}
            </Text>

            <Group justify="center" gap="md" mt="lg">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              <Button
                variant="filled"
                onClick={onDownload}
                disabled={batchDownloadState.completedBatches === 0}>
                Download Available Reports
              </Button>
            </Group>

            {batchDownloadState.completedBatches > 0 && (
              <Text size="xs" c="dimmed" ta="center" mt="sm">
                {batchDownloadState.currentBatch} out of {batchDownloadState.totalBatches} batches
                completed successfully
              </Text>
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <CustomModal
        modalProps={{
          opened: batchDownloadState.showModal,
          onClose: handleCloseAttempt,
          size: 'lg',
          closeOnClickOutside: false,
          closeOnEscape: true
        }}
        header={{ title: getModalTitle() }}>
        <Stack align="center" gap="md">
          {renderModalContent()}
        </Stack>
      </CustomModal>

      {/* Confirmation Modal */}
      <CustomModal
        modalProps={{
          opened: showConfirmation,
          onClose: handleCancelClose,
          size: 'md',
          closeOnClickOutside: false,
          closeOnEscape: true
        }}
        header={{ title: 'Cancel Download?' }}>
        <Stack gap="md">
          <Text size="md" ta="center" mt="md">
            The download is still in progress. Are you sure you want to cancel and close this
            window?
          </Text>
          <Text size="sm" c="dimmed" ta="center">
            This will stop the current download process.
          </Text>
          <Group justify="center" gap="md" mt="lg">
            <Button variant="outline" onClick={handleCancelClose}>
              Continue Download
            </Button>
            <Button variant="filled" color="red" onClick={handleConfirmClose}>
              Cancel & Close
            </Button>
          </Group>
        </Stack>
      </CustomModal>
    </>
  );
};
